import { useTranslation } from "react-i18next";
import type { OrganisationPersonUsageResponse } from "../../api/organisationReporting";
import { BreakdownPanel } from "./OrganisationSpendSummary";
import { personRows, sectionIds, topTenth } from "./organisationInsights";

/**
 * Usage by person for the period, at the foot of the Organisation page: how concentrated the spend is, then the people who spent
 * most, each with their share and the tool most of it went on, and everyone else in a dialog. It names individuals, so the page
 * renders it only for a person who also holds `consumption.read.others`.
 *
 * @param props.response - The loaded per-person usage response.
 */
export function OrganisationPersonUsage({ response }: { response: OrganisationPersonUsageResponse }): React.JSX.Element {
  const { t } = useTranslation();
  const rows = personRows(response.people, response.vendors, (tool) => t("organisation.ranked.mainTool", { tool }));
  const tenth = topTenth(response.people);

  return (
    <BreakdownPanel
      id={sectionIds.person}
      icon="person"
      title={t("organisation.byPerson")}
      subtitle={t("organisation.sections.person")}
      headline={tenth ? t("organisation.headline.topTenth", { count: tenth.people, percent: Math.round(tenth.sharePercent) }) : null}
      rows={rows}
      currency={response.currency}
      unitName={t("organisation.units.people")}
      labelHeader={t("organisation.table.person")}
      fileName="organisation-by-person.csv"
      comparedOver={null}
      empty={t("organisation.noPersonSpend")}
    />
  );
}
